/**
 * "Add to home screen" / Install-on-phone helpers.
 *
 * iOS doesn't fire `beforeinstallprompt` - we just show users how to do it
 * via the Share sheet. Android Chrome (and friends) fire the event; we
 * stash it on first emit and replay it from the settings panel.
 */

import { detectPlatform } from "./directions";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

let stashedPrompt: BeforeInstallPromptEvent | null = null;

export function captureBeforeInstallPrompt(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (e: Event) => {
    e.preventDefault();
    stashedPrompt = e as BeforeInstallPromptEvent;
  });
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari uses navigator.standalone
  const navStandalone = (window.navigator as { standalone?: boolean }).standalone;
  return navStandalone === true;
}

export type InstallHint =
  | { kind: "none" }
  | { kind: "ios-share-sheet" }
  | { kind: "android-prompt"; trigger: () => Promise<"accepted" | "dismissed"> };

export function getInstallHint(): InstallHint {
  if (isStandalone()) return { kind: "none" };

  const platform = detectPlatform();
  if (platform === "ios") {
    return { kind: "ios-share-sheet" };
  }

  if (stashedPrompt) {
    const evt = stashedPrompt;
    return {
      kind: "android-prompt",
      trigger: async () => {
        await evt.prompt();
        const result = await evt.userChoice;
        stashedPrompt = null; // single-use
        return result.outcome;
      },
    };
  }

  return { kind: "none" };
}

// Test-only resetters
export function _resetForTests(): void {
  stashedPrompt = null;
}
